"use client";

/**
 * P2-06f · Dismissible promo wrapper (client).
 *
 * Renders the promo with a close button; once dismissed it stays hidden for the
 * session (`sessionStorage`, keyed by the block instance id, so two promos
 * never share a dismissal and editing copy doesn't resurrect one). Children render on
 * the server and on first mount, so a non-dismissed promo never flashes empty;
 * only a promo already dismissed this session hides on mount.
 */
import { useEffect, useState } from "react";
import { useTranslator } from "~/i18n/provider";

const KEY_PREFIX = "promo-dismissed:";

export function PromoDismiss({
  dismissKey,
  children,
}: {
  dismissKey: string;
  children: React.ReactNode;
}) {
  const t = useTranslator();
  const [dismissed, setDismissed] = useState(false);
  const storageKey = `${KEY_PREFIX}${dismissKey}`;

  useEffect(() => {
    if (sessionStorage.getItem(storageKey) === "1") setDismissed(true);
  }, [storageKey]);

  if (dismissed) return null;

  return (
    <div className="relative">
      {children}
      <button
        type="button"
        aria-label={t("blocks.dismissPromotion")}
        onClick={() => {
          sessionStorage.setItem(storageKey, "1");
          setDismissed(true);
        }}
        className="absolute right-3 top-3 z-[2] inline-flex size-8 items-center justify-center rounded-full bg-black/25 text-white transition-colors hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden>
          <path
            d="M5 5l10 10M15 5L5 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}
